import { useEffect } from 'react';
import useSeoSettings from '../hooks/useSeoSettings';

/**
 * SEOHead — drop-in component that manages the document <title> and the
 * SEO-relevant meta tags (description, canonical, Open Graph, Twitter Card)
 * for whatever page mounts it. No third-party dependency: directly
 * manipulates the DOM in a useEffect, with a cleanup that does NOT undo
 * the values (the next page's SEOHead overwrites them in turn).
 *
 * Props:
 *   - title       : page-specific title (will be combined with the site
 *                   title as "<title> | <site>" unless `titleOverride` set)
 *   - description : page-specific meta description (≤160 chars recommended)
 *   - image       : page-specific Open Graph image URL (full URL)
 *   - canonical   : explicit canonical URL; defaults to current location
 *   - titleOverride : when true, use `title` verbatim and don't append the
 *                     site title
 *   - noindex     : when true, emit <meta name="robots" content="noindex">
 *
 * Falls back to the site-wide defaults from `seo_site_title` /
 * `seo_default_description` / `seo_default_og_image` for any prop that
 * isn't supplied.
 *
 * Convention: render exactly one SEOHead per page, near the top of the
 * page's component tree. SSR is not supported here — these tags are set
 * client-side after hydration. For first-paint search indexing we rely
 * on Googlebot's JavaScript rendering, which is reliable in 2026 for
 * crawl-budget-typical sites.
 */
const SEOHead = ({
  title,
  description,
  image,
  canonical,
  titleOverride = false,
  noindex = false,
}) => {
  const seo = useSeoSettings();

  useEffect(() => {
    const siteTitle = seo.seo_site_title || seo.business_name || 'MorphBook';
    const defaultDesc = seo.seo_default_description || '';
    const defaultImage = seo.seo_default_og_image || seo.business_logo_url || '';

    // Compose final values with sensible fallback chain.
    const finalTitle = titleOverride
      ? (title || siteTitle)
      : (title ? `${title} | ${siteTitle}` : siteTitle);
    const finalDesc = (description || defaultDesc || '').toString().slice(0, 300);
    const finalImage = image || defaultImage || '';
    const finalCanonical = canonical
      || (typeof window !== 'undefined'
        ? `${seo.site_origin || window.location.origin}${window.location.pathname}`
        : '');

    document.title = finalTitle;

    setMeta({ name: 'description' }, finalDesc);
    setMeta({ name: 'robots' }, noindex ? 'noindex,nofollow' : 'index,follow');

    // Open Graph
    setMeta({ property: 'og:type' }, 'website');
    setMeta({ property: 'og:title' }, finalTitle);
    setMeta({ property: 'og:description' }, finalDesc);
    if (finalImage) setMeta({ property: 'og:image' }, finalImage);
    if (finalCanonical) setMeta({ property: 'og:url' }, finalCanonical);
    if (siteTitle) setMeta({ property: 'og:site_name' }, siteTitle);

    // Twitter Card — large image when we have one, summary otherwise.
    setMeta({ name: 'twitter:card' }, finalImage ? 'summary_large_image' : 'summary');
    setMeta({ name: 'twitter:title' }, finalTitle);
    setMeta({ name: 'twitter:description' }, finalDesc);
    if (finalImage) setMeta({ name: 'twitter:image' }, finalImage);

    // Canonical link
    setLink({ rel: 'canonical' }, finalCanonical);

    // No cleanup — the next page's SEOHead overwrites these values. We
    // deliberately don't restore previous values because between two
    // SEOHead-bearing pages there's no useful "previous" to fall back to.
  }, [seo, title, description, image, canonical, titleOverride, noindex]);

  return null;
};

/**
 * Find or create a <meta> tag matching `attrs` (e.g. {name: 'description'} or
 * {property: 'og:title'}) and set its `content` attribute. Idempotent — a
 * second call with different content overwrites.
 */
const setMeta = (attrs, content) => {
  if (typeof document === 'undefined') return;
  const selector = Object.entries(attrs)
    .map(([k, v]) => `[${k}="${cssEscape(v)}"]`)
    .join('');
  let el = document.head.querySelector(`meta${selector}`);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
};

const setLink = (attrs, href) => {
  if (typeof document === 'undefined') return;
  if (!href) return;
  const selector = Object.entries(attrs)
    .map(([k, v]) => `[${k}="${cssEscape(v)}"]`)
    .join('');
  let el = document.head.querySelector(`link${selector}`);
  if (!el) {
    el = document.createElement('link');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// Tiny CSS.escape polyfill for older browsers; only handles the values we
// use (ASCII attribute values).
const cssEscape = (v) => String(v).replace(/"/g, '\\"');

export default SEOHead;
